import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import moment from 'moment-timezone';
import { ApiService } from './api.service';
import { DatastoreService } from './datastore.service';
import { FIRST_TOAST_GUEST_NOTICE, requiresFirstToastGuestNotice } from './first-toast-notice';

export interface ExtensionOptionMeta {
  value: '30min' | '1hour';
  label: string;
  shortLabel: string;
  minutes: number;
  /** Looked up from BookingBug extras at load time. Null until loaded / when missing. */
  extra: ExtraItem | null;
  /** Room-hire fee at the extended duration, from fees(). Filled by loadExtensionPricing(). */
  newRoomHireCost: number;
  /** Charge for this option = newRoomHireCost − existing room-hire cost (never negative). */
  cost: number;
  /**
   * True when /fees has no price for this option's extended duration. Distinct
   * from `cost === 0`, which is a genuinely free extension. Callers MUST NOT
   * offer or charge for an unpriced option.
   */
  unpriced?: boolean;
}

export interface ExtraItem {
  item_id: string;
  item_name: string;
  item_description: string;
  item_price: number;
  global_service_id?: string | number;
  data?: { pmtTyp?: string; description?: string; [key: string]: any };
}

export interface DrinkOption {
  item_id: string;
  item_name: string;
  item_price: number;
  description: string;
  selected: boolean;
  quantity: number;
}

export type SummaryConfirmation = 'confirm' | 'enquire' | 'leave' | null;

/** One extra-item link created post-payment (mirrors the pendingExtendLinks shape). */
export interface ExtendLink {
  item_id: string;
  data: { item: string; cost?: number; quantity?: number; pmtTyp?: string; [key: string]: any };
}

/**
 * Snapshot of the user's request, persisted to localStorage just before the
 * summary page either redirects to HeyCentric or navigates to the enquiry
 * confirmation. Both confirmation pages read this back in ngOnInit because the
 * in-memory ExtendStateService is wiped by the external payment redirect.
 */
export interface ExtendSummarySnapshot {
  path: 'confirm' | 'enquire';
  bookingReference?: string;
  extensionSummary: string;
  confettiSummary: string;
  firstToastSummary: string;
  waterCordialSummary: string;
  certificatesSummary: string;
  totalAdditionalCost: number;
}

interface ServiceTimesSlot {
  start: string;
  available: boolean;
}

interface ServiceTimesResponse {
  times?: ServiceTimesSlot[];
}

export const EXTENSION_DESCRIPTION_30 = 'Extended ceremony time (30 minutes)';
export const EXTENSION_DESCRIPTION_60 = 'Extended ceremony time (60 minutes)';
/** Extra-item descriptions that mark a booking as already extended. */
export const EXTENSION_ITEM_DESCRIPTIONS = [
  EXTENSION_DESCRIPTION_30,
  EXTENSION_DESCRIPTION_60,
];
const CONFETTI_FIVE_PACK = 'Confetti - for five sachets';
const CONFETTI_SINGLE = 'Confetti - per sachet';
const CERTIFICATES_DESCRIPTION = 'Additional Certificates added during booking';
const DRINKS_DESCRIPTION = 'Drinks';
const FIRST_TOAST_DESCRIPTION = 'First toast';
const WATER_OR_CORDIAL_NAME = 'Water or cordial';
export const ROOM_HIRE_DESCRIPTION = 'Room Hire fee';
// Field on the fees() response that carries the room-hire fee for the queried
// date (same mapping the reschedule journey uses for "Room Hire fee").
const ROOM_HIRE_FEES_FIELD = 'hire_fee_vat_s';

// Recipients for the extend enquiry notification (RWRSPI-971). The register
// office address is pulled from config via DatastoreService.registerOfficeEmail
// so it can be changed without a front-end deploy.
const BOOKINGLAB_NOTIFICATIONS_EMAIL = 'notifications@bookinglab.co.uk';
const FROM_EMAIL_ADDRESS = 'registeroffice@westminster.gov.uk';

@Injectable({ providedIn: 'root' })
export class ExtendStateService {
  readonly MAX_CONFETTI = 100;
  readonly MAX_CERTIFICATES = 20;
  readonly MAX_DRINK_QTY = 10;

  // Hardcoded labels — prices and item_ids come from the matched extras below.
  readonly extensionOptions: ExtensionOptionMeta[] = [
    { value: '30min', label: 'Bespoke Plus - extend by 30 minutes',  shortLabel: '30 minutes', minutes: 30, extra: null, newRoomHireCost: 0, cost: 0 },
    { value: '1hour', label: 'Bespoke Extra - extend by 60 minutes', shortLabel: '1 hour',     minutes: 60, extra: null, newRoomHireCost: 0, cost: 0 },
  ];

  extensionOption: '30min' | '1hour' | null = null;
  summaryConfirmation: SummaryConfirmation = null;
  /**
   * How the customer wants to pay when the booking carries an outstanding
   * balance: settle everything ('full') or only the extension + add-ons
   * ('new'). Null until chosen; only asked when a balance is outstanding.
   */
  paymentChoice: 'full' | 'new' | null = null;

  /** Add-ons page state — Figma design: three independent sections. */
  confettiSelected = false;
  confettiQuantity: number | null = null;

  firstToastSelected = false;
  /** item_id → quantity. Excludes the water-or-cordial item (held separately). */
  drinkSelections: Record<string, number> = {};
  /** Starts unanswered (null) — an unanswered radio is treated the same as No. */
  waterOrCordialSelected: 'yes' | 'no' | null = null;

  certificatesSelected = false;
  certificatesQuantity: number | null = null;

  // Discovered extras — populated by loadExtras(). Components read these to render
  // hints, prices and post-payment link payloads.
  extrasLoaded = false;
  private extrasLoadingPromise: Promise<void> | null = null;
  /** Which booking the extras/pricing caches were resolved for. */
  private cacheBookingId: any = null;
  /** The booking's extra-item links, fetched once per booking and shared. */
  private bookingLinks: any[] = [];
  confettiFivePackExtra: ExtraItem | null = null;
  confettiSingleExtra: ExtraItem | null = null;
  certificatesExtra: ExtraItem | null = null;
  drinkExtras: DrinkOption[] = [];
  waterOrCordialExtra: ExtraItem | null = null;
  firstToastExtra: ExtraItem | null = null;
  /** The "Room Hire fee" extra on the booked service (gives the link's item_id + data). */
  roomHireExtra: ExtraItem | null = null;

  // Extension cost — the difference between the room-hire fee at the extended
  // duration (from fees()) and the room-hire cost already on the booking.
  // Priced up front for BOTH options by loadExtensionPricing() (one fees() call
  // per duration) so each radio can show its cost without a per-click fetch.
  // The selected-option figures are exposed via the extensionCost / roomHireNewCost
  // getters below.
  pricingLoaded = false;
  private pricingLoadingPromise: Promise<void> | null = null;
  /** The existing "Room Hire fee" link on the booking (carries old cost + data). */
  roomHireLink: any = null;
  /** Existing room-hire cost on the booking (the "old" fee we diff against). */
  roomHireOldCost = 0;
  /** Room-hire fee for the booking's date at its CURRENT duration (old-cost fallback). */
  baseHireFee = 0;
  /** Whether the booking's date falls in high season (from fees.is_high_season). */
  isHighSeason: boolean | null = null;
  /**
   * True when /fees could not price ANY extension option, so the extend journey
   * cannot quote a price at all. Components must block progression on this
   * rather than showing £0 — see the `unpriced` note on ExtensionOptionMeta.
   */
  pricingUnavailable = false;

  // Availability check (drives summary radio variant + add-ons page gate).
  availabilityConfirmed: boolean | null = null;
  checkingAvailability = false;
  private latestCheckId = 0;

  constructor(
    private apiService: ApiService,
    private datastore: DatastoreService,
  ) {}

  /**
   * The extras/pricing caches are only valid for the booking they were
   * resolved against. A stale cache from another booking in the same SPA
   * session prices the extension off the wrong catalogue (wrong room-hire
   * extra → roomHireLink never matches → wrong base fee, and the post-payment
   * room-hire update targets a link that doesn't exist).
   */
  private invalidateCachesIfBookingChanged(): void {
    const bookingId = this.datastore.booking?.id ?? null;
    if (bookingId === this.cacheBookingId) return;
    this.cacheBookingId = bookingId;
    this.extrasLoaded = false;
    this.pricingLoaded = false;
    this.bookingLinks = [];
    this.roomHireLink = null;
    this.roomHireExtra = null;
    // Clear the unpriced markers too, or a booking whose fees failed to load
    // leaves the next booking's options looking unpriceable.
    this.pricingUnavailable = false;
    this.extensionOptions.forEach((option) => {
      option.unpriced = false;
    });
  }

  /**
   * Fetches the booked service's extras and slots them into the typed fields
   * above. Idempotent per booking — concurrent callers share the same
   * in-flight promise, and a booking change invalidates the cache.
   */
  async loadExtras(): Promise<void> {
    this.invalidateCachesIfBookingChanged();
    if (this.extrasLoaded) return;
    if (this.extrasLoadingPromise) return this.extrasLoadingPromise;

    this.extrasLoadingPromise = this.doLoadExtras();
    try {
      await this.extrasLoadingPromise;
    } finally {
      this.extrasLoadingPromise = null;
    }
  }

  private async doLoadExtras(): Promise<void> {
    const booking = this.datastore.booking;
    if (!booking) {
      console.warn('ExtendStateService.loadExtras: missing booking');
      return;
    }

    // Resolve the catalogue from the booking's OWN links — selectedService /
    // localStorage can carry another booking's service in the same browser
    // session, and a mismatched catalogue silently breaks room-hire matching
    // and extension pricing.
    try {
      const links = (await this.apiService.getServiceItemExtraLinksForBooking(
        booking.company_id,
        booking.id,
      )) as any;
      this.bookingLinks = Array.isArray(links) ? links : [];
    } catch (err) {
      console.error('ExtendStateService.loadExtras: failed to fetch booking links', err);
      this.bookingLinks = [];
    }
    const globalServiceId =
      this.bookingLinks[0]?.global_service_id ??
      this.datastore.selectedService?.global_id ??
      booking?.service_id;
    if (!globalServiceId) {
      console.warn('ExtendStateService.loadExtras: missing global_service_id');
      return;
    }

    let extras: ExtraItem[];
    try {
      extras = (await firstValueFrom(
        this.apiService.getServiceItemExtras(
          booking.company_id,
          globalServiceId,
          undefined,
          true,
        ),
      )) as ExtraItem[];
    } catch (err) {
      console.error('ExtendStateService.loadExtras: failed to fetch extras', err);
      return;
    }

    const byDescription = (description: string): ExtraItem | null =>
      extras.find((e) => e?.item_description === description) ?? null;

    // Extension fees — Chad added these for Westminster room marriage. Other
    // rooms may not have them yet; the option is treated as unavailable when
    // the matching extra is missing.
    this.extensionOptions[0].extra = byDescription(EXTENSION_DESCRIPTION_30);
    this.extensionOptions[1].extra = byDescription(EXTENSION_DESCRIPTION_60);

    this.confettiFivePackExtra = byDescription(CONFETTI_FIVE_PACK);
    this.confettiSingleExtra = byDescription(CONFETTI_SINGLE);
    this.certificatesExtra = byDescription(CERTIFICATES_DESCRIPTION);
    this.roomHireExtra = byDescription(ROOM_HIRE_DESCRIPTION);

    // No Friday/Saturday gate here: every extension is a Bespoke Plus (30
    // min) / Bespoke Extra (60 min) booking, and bespoke bookings may have
    // the First toast on any day — anyone reaching the extend add-ons page
    // is buying one (the enquiry path skips add-ons entirely).
    this.firstToastExtra = byDescription(FIRST_TOAST_DESCRIPTION);

    const allDrinks = extras.filter((e) => e?.item_description === DRINKS_DESCRIPTION);
    this.waterOrCordialExtra = allDrinks.find((e) => e?.item_name === WATER_OR_CORDIAL_NAME) ?? null;
    this.drinkExtras = allDrinks
      .filter((e) => e?.item_name !== WATER_OR_CORDIAL_NAME)
      .map((e) => ({
        item_id: String(e.item_id),
        item_name: String(e.item_name ?? ''),
        item_price: Number(e.item_price ?? 0),
        description: typeof e?.data?.description === 'string' ? e.data.description : '',
        selected: !!this.drinkSelections[String(e.item_id)] && this.drinkSelections[String(e.item_id)] > 0,
        quantity: this.drinkSelections[String(e.item_id)] && this.drinkSelections[String(e.item_id)] > 0
          ? Math.min(this.drinkSelections[String(e.item_id)], this.MAX_DRINK_QTY)
          : 1,
      }));

    this.extrasLoaded = true;
  }

  /**
   * Calls the times API for the slot immediately following the booking end
   * and sets `availabilityConfirmed` based on whether the registrar can fit
   * the selected extension. Falls back to `false` (enquiry path) on any
   * missing data or API error. Uses `latestCheckId` so out-of-order responses
   * from rapid radio toggling don't overwrite the most recent result.
   */
  async checkAvailability(): Promise<void> {
    const checkId = ++this.latestCheckId;
    const isStale = () => checkId !== this.latestCheckId;
    const log = (...args: any[]) => console.log(`[ExtendAvailability #${checkId}]`, ...args);

    this.checkingAvailability = true;
    this.availabilityConfirmed = null;

    try {
      const booking = this.datastore.booking;
      const extension = this.selectedExtension;
      log('Starting availability check', {
        bookingId: booking?.id,
        selectedExtensionOption: this.extensionOption,
        extensionMinutes: extension?.minutes ?? null,
      });

      if (!booking || !extension) {
        log('Unavailable: missing booking or selected extension', {
          hasBooking: !!booking,
          hasExtension: !!extension,
        });
        if (!isStale()) this.availabilityConfirmed = false;
        return;
      }

      const resourceId = booking?._links?.resource?.href?.split('/').pop() ?? null;
      if (!resourceId) {
        log('Unavailable: could not derive resourceId from booking._links.resource.href', {
          resourceHref: booking?._links?.resource?.href ?? null,
        });
        if (!isStale()) this.availabilityConfirmed = false;
        return;
      }

      // booking.duration is in seconds on this endpoint; normalise to minutes.
      const currentBookingMinutes = (Number(booking.duration) || 3600) / 60;
      const extensionMinutes = extension.minutes;
      // Check the full booking we're about to write (current + extension) at its
      // original start, passing ignore_booking so this booking's own slot isn't
      // counted as busy.
      const newDurationMinutes = currentBookingMinutes + extensionMinutes;

      // parseZone keeps the booking's original UTC offset so logs read locally.
      const bookingStart = moment.parseZone(booking.datetime);
      const dateOnly = bookingStart.format('YYYY-MM-DD');
      log('Checking availability for the full extended booking at its original start', {
        bookingDatetime: booking.datetime,
        rawBookingDuration: booking.duration,
        currentBookingMinutes,
        extensionMinutes,
        newDurationMinutes, // <-- the duration we now send to the times endpoint
        bookingStart: bookingStart.format(), // local offset
        bookingStartUtc: bookingStart.toISOString(), // same instant, UTC
        dateQueried: dateOnly,
        resourceId,
        serviceId: booking.service_id,
        ignoreBooking: booking.id,
      });

      const response = (await this.apiService.getServiceTimes(
        booking.company_id,
        booking.service_id,
        dateOnly,
        dateOnly,
        resourceId,
        newDurationMinutes,
        booking.id, // ignore_booking — don't count this booking's own slot as busy
      )) as ServiceTimesResponse;
      const times = response?.times ?? [];
      log(`Times API returned ${times.length} slot(s)`, times);

      const match = times.find((t: ServiceTimesSlot) => moment(t.start).isSame(bookingStart));
      log('Matched the booking start against returned times', {
        requiredStart: bookingStart.format(), // local offset
        requiredStartUtc: bookingStart.toISOString(), // same instant, UTC
        matchFound: !!match,
        matchedSlot: match ?? null,
        matchAvailable: match?.available ?? null,
      });

      if (!isStale()) {
        this.availabilityConfirmed = !!(match && match.available);
        log(
          this.availabilityConfirmed
            ? `RESULT: available — the resource can host the full ${newDurationMinutes}-min booking at its original start, extension can be offered`
            : 'RESULT: unavailable — the full extended booking does not fit at the original start, falling back to enquiry path',
          { availabilityConfirmed: this.availabilityConfirmed },
        );
      } else {
        log('Result discarded — a newer check superseded this one (stale)');
      }

      // Pricing for both options was resolved up front by loadExtensionPricing()
      // on page init, so the selected option's cost (extensionCost getter) is
      // already available — no per-click fees() call needed here. Ensure it's
      // loaded in case availability is somehow checked before init completes.
      await this.loadExtensionPricing();
    } catch (err) {
      console.error(`[ExtendAvailability #${checkId}] Failed to check extend availability`, err);
      if (!isStale()) this.availabilityConfirmed = false;
    } finally {
      if (!isStale()) this.checkingAvailability = false;
    }
  }

  /**
   * Prices BOTH extension options up front so each radio on changes-and-cost can
   * show its cost without a per-click fetch. The fees() API prices extended
   * durations directly (including any seasonal uplift), so each option is one
   * fees() call at the booking's current duration + the extension length
   * (e.g. 90 min for Bespoke Plus, 120 min for Bespoke Extra on a standard
   * 60-min booking):
   *   option.newRoomHireCost = fees(currentDuration + option.minutes).hire_fee_vat_s
   *   option.cost            = newRoomHireCost − (room-hire cost already on the booking)
   * Idempotent — concurrent callers share the same in-flight promise. On any
   * missing data / API error the option costs are left at 0.
   */
  async loadExtensionPricing(): Promise<void> {
    this.invalidateCachesIfBookingChanged();
    if (this.pricingLoaded) return;
    if (this.pricingLoadingPromise) return this.pricingLoadingPromise;

    this.pricingLoadingPromise = this.doLoadExtensionPricing();
    try {
      await this.pricingLoadingPromise;
    } finally {
      this.pricingLoadingPromise = null;
    }
  }

  private async doLoadExtensionPricing(): Promise<void> {
    const booking = this.datastore.booking;
    if (!booking) return;

    await this.loadExtras();
    if (!this.roomHireExtra) {
      console.warn('ExtendStateService.loadExtensionPricing: no "Room Hire fee" extra on the service — cannot price extension');
      return;
    }

    // Same source of truth as loadExtras: the booking's own links name the
    // service the fees must be priced against.
    const serviceId =
      this.bookingLinks[0]?.global_service_id ??
      this.datastore.selectedService?.global_id ??
      booking.service_id;
    const dateOnly = moment.parseZone(booking.datetime).format('YYYY-MM-DD');
    const currentDurationMinutes = this.toMinutes(Number(booking.duration));

    const fetchFees = async (duration: number): Promise<any | null> => {
      try {
        const fees = await this.apiService.fees(booking.company_id, dateOnly, serviceId, duration);
        return fees && typeof fees === 'object' ? fees : null;
      } catch (err: any) {
        // 404 means no fee row exists for this duration — the ceremony has no
        // configured price. Anything else is a transport/server failure. Both
        // are unpriceable, but only the 404 is expected, so log accordingly.
        if (err?.status === 404) {
          console.warn(
            `ExtendStateService.loadExtensionPricing: no fees configured for service ${serviceId} at duration=${duration} — option cannot be priced`,
          );
        } else {
          console.error(`ExtendStateService.loadExtensionPricing: fees(duration=${duration}) failed`, err);
        }
        return null;
      }
    };

    // The fees API prices extended durations directly (same contract the public
    // booking app uses via calculated_duration = base + add_duration), so each
    // option's new room-hire fee comes from a fees() call at the extended
    // duration. The current-duration call only feeds the old-cost fallback and
    // the drift diagnostics below.
    const [baseFees, ...optionFees] = await Promise.all([
      fetchFees(currentDurationMinutes),
      ...this.extensionOptions.map((option) => fetchFees(currentDurationMinutes + option.minutes)),
    ]);

    // Mark every option that came back unpriced BEFORE the all-unpriced bail-out,
    // so a partial failure (Bespoke Plus priced, Bespoke Extra not) still flags the
    // option that has no price instead of leaving it looking free.
    this.extensionOptions.forEach((option, index) => {
      option.unpriced = !optionFees[index];
    });

    if (optionFees.every((fees) => !fees)) {
      console.warn('ExtendStateService.loadExtensionPricing: no fees() response for any extended duration — cannot price extension');
      // Previously returned with pricingLoaded still false and every cost at 0,
      // so the UI offered both tiers at £0. Flag it so components can block.
      this.pricingUnavailable = true;
      this.extensionOptions.forEach((option) => {
        option.newRoomHireCost = 0;
        option.cost = 0;
      });
      return;
    }

    this.pricingUnavailable = false;
    this.isHighSeason = baseFees ? !!baseFees.is_high_season : null;
    this.baseHireFee = Number(baseFees?.[ROOM_HIRE_FEES_FIELD] ?? 0);

    // Old cost = the room-hire link currently on the booking. Fall back to the
    // fees base if no link is found (shouldn't happen for a ceremony booking).
    this.roomHireLink = await this.loadRoomHireLink();
    this.roomHireOldCost = this.roomHireLink?.data?.cost != null
      ? Number(this.roomHireLink.data.cost)
      : this.baseHireFee;

    this.extensionOptions.forEach((option, index) => {
      const fees = optionFees[index];
      if (!fees) {
        // Unpriced (flagged above). Leave the figures at 0 but never present them
        // as a real quote — `unpriced` is what components must gate on.
        option.newRoomHireCost = 0;
        option.cost = 0;
        return;
      }
      option.newRoomHireCost = this.round2(Number(fees[ROOM_HIRE_FEES_FIELD] ?? 0));
      option.cost = Math.max(0, this.round2(option.newRoomHireCost - this.roomHireOldCost));
    });

    console.log('ExtendStateService.loadExtensionPricing resolved', {
      isHighSeason: this.isHighSeason,
      currentDurationMinutes,
      baseHireFee: this.baseHireFee,
      roomHireOldCost: this.roomHireOldCost,
      options: this.extensionOptions.map((o) => ({ value: o.value, newRoomHireCost: o.newRoomHireCost, cost: o.cost })),
    });

    this.pricingLoaded = true;
  }

  /** Returns the booking's room-hire link (links come from doLoadExtras' fetch). */
  private async loadRoomHireLink(): Promise<any | null> {
    const booking = this.datastore.booking;
    if (!booking || !this.roomHireExtra) return null;
    let arr = this.bookingLinks;
    if (!arr || arr.length === 0) {
      try {
        const links = (await this.apiService.getServiceItemExtraLinksForBooking(
          booking.company_id,
          booking.id,
        )) as any;
        arr = Array.isArray(links) ? links : [];
        this.bookingLinks = arr;
      } catch (err) {
        console.error('ExtendStateService.loadRoomHireLink: failed to fetch links', err);
        return null;
      }
    }
    return arr.find((l) => String(l?.item_id) === String(this.roomHireExtra!.item_id)) ?? null;
  }

  /**
   * Payload for the post-payment room-hire link update (PUT). Overwrites the
   * existing link's cost with the new room-hire fee, preserving the rest of its
   * data block. Returns null when there is nothing to change.
   */
  buildRoomHireUpdate(settleBalance = true): { item_id: string; global_service_id: any; data: any } | null {
    if (!this.roomHireExtra || this.extensionCost <= 0) return null;
    const booking = this.datastore.booking;
    // Prefer the link's own global_service_id — it is authoritative for the
    // booking; selectedService can be stale in a shared session.
    const serviceId =
      this.roomHireLink?.global_service_id ??
      this.datastore.selectedService?.global_id ??
      booking?.service_id;
    const existingData = this.roomHireLink?.data ?? this.roomHireExtra.data ?? {};
    const itemId = String(this.roomHireLink?.item_id ?? this.roomHireExtra.item_id);
    // When the payment settles the whole booking the line is fully paid at
    // its new cost. When the customer pays only the new charges, the gateway
    // charges exactly extensionCost, so the line's paid moves up by that
    // amount — any unpaid remainder on the original room hire stays
    // outstanding. Clamped to the new line total for already-fully-paid lines.
    const quantity = Number(existingData.quantity) || 1;
    const newLineTotal = this.round2(this.roomHireNewCost * quantity);
    const newPaid = settleBalance
      ? newLineTotal
      : Math.min(
          this.round2((Number(existingData.paid) || 0) + this.extensionCost),
          newLineTotal,
        );
    return {
      item_id: itemId,
      global_service_id: serviceId,
      data: { ...existingData, cost: this.roomHireNewCost, paid: newPaid },
    };
  }

  /** Charge for the selected extension (room-hire difference). 0 when none chosen. */
  get extensionCost(): number {
    return this.selectedExtension?.cost ?? 0;
  }

  /** New room-hire fee for the selected extension (used by buildRoomHireUpdate). */
  get roomHireNewCost(): number {
    return this.selectedExtension?.newRoomHireCost ?? 0;
  }

  private round2(value: number): number {
    return Math.round((Number(value) || 0) * 100) / 100;
  }

  /**
   * booking.duration arrives in different units depending on the endpoint:
   * the single-booking GET returns minutes, the list endpoint seconds
   * (RWRSPI-968). A ceremony is 30–90 minutes, so anything above 300 is
   * seconds and is converted.
   */
  private toMinutes(duration: number): number {
    if (!duration || Number.isNaN(duration)) return 0;
    return duration > 300 ? Math.round(duration / 60) : Math.round(duration);
  }

  // Sends the enquiry emails to the member and the office on every visit to the
  // confirmation page. Failures are logged, not surfaced to the user.
  async sendEnquiryEmails(): Promise<void> {
    console.log('[enquiry-email] sendEnquiryEmails() called');
    const booking = this.datastore.booking;
    if (!booking?.id || !booking?.company_id) {
      console.warn('[enquiry-email] BAILED: missing booking id/company_id', {
        booking,
        id: booking?.id,
        company_id: booking?.company_id,
      });
      return;
    }

    const memberEmail = this.datastore.member?.email;
    if (!memberEmail) {
      console.warn('[enquiry-email] BAILED: no member email on datastore.member', {
        member: this.datastore.member,
      });
      return;
    }

    try {
      console.log('[enquiry-email] sending request #1 (to member)', { company_id: booking.company_id, to: memberEmail });
      await this.apiService.sendExtendedCeremonyEnquiryEmail(
        booking.company_id,
        memberEmail,
        FROM_EMAIL_ADDRESS,
        booking.id
      );
      console.log('[enquiry-email] request #1 done; sending request #2 (to office)');
      const body = this.renderEnquiryAdminTemplate(
        this.datastore.extendEnquiryAdminEmailTemplate,
        booking
      );
      await this.apiService.sendExtendedCeremonyAdminEnquiryEmail(
        booking.company_id,
        booking.id,
        this.datastore.registerOfficeEmail,
        FROM_EMAIL_ADDRESS,
        this.datastore.extendEnquiryAdminEmailSubject,
        body
      );
      console.log('[enquiry-email] both requests sent OK');

      // Record the customer email on the booking timeline.
      try {
        await this.apiService.bookingTimeline(
          booking.company_id,
          booking.purchase_id,
          this.datastore.member?.id,
          memberEmail,
          'Extended ceremony enquiry',
          BOOKINGLAB_NOTIFICATIONS_EMAIL,
        );
        console.log('[enquiry-email] booking timeline interaction logged');
      } catch (timelineErr) {
        console.error('[enquiry-email] FAILED to log booking timeline', timelineErr);
      }
    } catch (err) {
      console.error('[enquiry-email] FAILED during send', err);
    }
  }

  // Fills the admin enquiry email template with values from the booking being extended.
  private renderEnquiryAdminTemplate(template: string, booking: any): string {
    const config = this.parsedConfig();
    const ourCeremonyQuestions = config.myCeremony['our-ceremony'].questions;

    const partnerOne = this.getBookingAnswer(booking, ourCeremonyQuestions.party1FirstName);
    const partnerTwo = this.getBookingAnswer(booking, ourCeremonyQuestions.party2FirstName);

    const localDateTime = moment.utc(booking.datetime).tz('Europe/London');
    const ceremonyDate = localDateTime.format('dddd D MMMM YYYY');
    const ceremonyTime = localDateTime.format('h:mma');

    let extensionRequested = this.extensionSummaryText;
    if (this.selectedExtension) {
      extensionRequested = this.selectedExtension.label;
    }

    let html = template;
    html = html.replace(/\{\{\s*booking\.date\s*\|date:[^}]*\}\}/gi, ceremonyDate);
    html = html.replace(/\{\{\s*booking\.time\s*\|time:[^}]*\}\}/gi, ceremonyTime);
    html = html.split('{{ extend_ref }}').join(booking.id);
    html = html.split('{{ extend_requested }}').join(extensionRequested);
    html = html.split('{{ booking.id }}').join(booking.id);
    html = html.split('{{ booking.partner_one_s_first_name }}').join(partnerOne);
    html = html.split('{{ booking.partner_two_s_first_name }}').join(partnerTwo);
    html = html.split('{{ booking.resource.name }}').join(booking.resource_name);
    return html;
  }

  // Emails the customer the add-ons they bought. Shared by the extend and
  // standalone add-ons journeys — pass the same links sent to the /link call.
  // Skips silently if there are no paid add-ons, no template, or no recipient.
  async sendAddOnEmail(
    booking: any,
    recipientEmail: string | undefined,
    links: ExtendLink[] = []
  ): Promise<void> {
    const addOnLinks = (links ?? []).filter((l) => l?.data?.item && Number(l.data?.cost ?? 0) > 0);
    if (!addOnLinks.length) {
      console.log('[addon-email] skipped — no paid add-ons on this booking');
      return;
    }
    if (!booking?.id || !booking?.company_id || !recipientEmail) {
      console.warn('[addon-email] skipped — missing booking or recipient', { id: booking?.id, recipientEmail });
      return;
    }

    const template = this.datastore.addOnEmailTemplate;
    if (!template) {
      console.warn('[addon-email] skipped — no addOnEmailTemplate in optional-addons config');
      return;
    }

    try {
      const body = this.renderAddOnTemplate(template, booking, addOnLinks);
      console.log('[addon-email] sending to', recipientEmail);
      await this.apiService.sendExtendedCeremonyAddOnEmail(
        booking.company_id,
        booking.id,
        recipientEmail,
        FROM_EMAIL_ADDRESS,
        this.datastore.addOnEmailSubject,
        body
      );

      // Log it against the booking timeline.
      await this.apiService.bookingTimeline(
        booking.company_id,
        booking.purchase_id,
        booking.client_id,
        recipientEmail,
        this.datastore.addOnEmailSubject,
        BOOKINGLAB_NOTIFICATIONS_EMAIL,
      );
    } catch (err) {
      console.error('[addon-email] failed to send', err);
    }
  }

  // Swaps the bindings in the optional-addons template: partner names, booking
  // id, and {{ itemsummary }} (the items + quantities the customer bought).
  private renderAddOnTemplate(template: string, booking: any, links: ExtendLink[]): string {
    const config = this.parsedConfig();
    const ourCeremonyQuestions = config?.myCeremony?.['our-ceremony']?.questions ?? {};

    const partnerOne = this.getBookingAnswer(booking, ourCeremonyQuestions.party1FirstName);
    const partnerTwo = this.getBookingAnswer(booking, ourCeremonyQuestions.party2FirstName);

    let html = template;
    html = html.split('{{ booking.id }}').join(booking.id);
    html = html.split('{{ booking.partner_one_s_first_name }}').join(partnerOne);
    html = html.split('{{ booking.partner_two_s_first_name }}').join(partnerTwo);
    html = html.split('{{ itemsummary }}').join(this.buildItemSummaryHtml(links));
    return html;
  }

  // One line per add-on: "Item name (x2)". Links are already filtered to paid items.
  private buildItemSummaryHtml(links: ExtendLink[]): string {
    return (links ?? [])
      .map((l) => `${l.data.item} (x${Number(l.data?.quantity ?? 1)})`)
      .join('<br>');
  }

  private getBookingAnswer(booking: any, questionId: any): string {
    if (booking.questions && booking.questions[questionId]) {
      return booking.questions[questionId].answer;
    }
    return '';
  }

  private parsedConfig(): any {
    try {
      return typeof this.datastore.config === 'string'
        ? JSON.parse(this.datastore.config)
        : this.datastore.config;
    } catch {
      return null;
    }
  }

  /** Westminster Room guest-capacity notice for the First toast section. */
  readonly firstToastGuestNotice = FIRST_TOAST_GUEST_NOTICE;

  get showFirstToastGuestNotice(): boolean {
    return requiresFirstToastGuestNotice(
      this.datastore.selectedService?.global_id ?? this.datastore.booking?.service_id,
    );
  }

  get selectedExtension(): ExtensionOptionMeta | null {
    return this.extensionOptions.find((o) => o.value === this.extensionOption) ?? null;
  }

  /**
   * The extension charge. No longer a flat extension-fee extra — it's the
   * room-hire difference resolved by checkAvailability() (see resolveExtensionCost).
   */
  get extensionPrice(): number {
    return this.extensionCost;
  }

  /**
   * HeyCentric revenue/fund code for the extension charge. The code lives on the
   * room-hire *service extra* (data.pmtTyp), NOT on the booking link — HeyCentric
   * rejects fee lines with a null rev code, so the gateway charge must read it
   * from here. The extension is additional room hire, so the room-hire code applies.
   */
  get roomHirePmtTyp(): string | undefined {
    return this.roomHireExtra?.data?.pmtTyp;
  }

  /**
   * Confetti split across 5-packs and singles to minimise cost. Returns
   * { fivePacks, singles, total } for both display and pending-link building.
   */
  computeConfettiBreakdown(quantity: number): { fivePacks: number; singles: number; total: number } {
    if (!this.confettiSelected || !quantity || quantity <= 0) {
      return { fivePacks: 0, singles: 0, total: 0 };
    }
    const fivePackPrice = Number(this.confettiFivePackExtra?.item_price ?? 0);
    const singlePrice = Number(this.confettiSingleExtra?.item_price ?? 0);

    if (this.confettiFivePackExtra && fivePackPrice > 0) {
      const fivePacks = Math.floor(quantity / 5);
      const singles = quantity - fivePacks * 5;
      const total = fivePacks * fivePackPrice + singles * singlePrice;
      return { fivePacks, singles, total };
    }
    return { fivePacks: 0, singles: quantity, total: quantity * singlePrice };
  }

  get confettiCost(): number {
    return this.computeConfettiBreakdown(this.confettiQuantity ?? 0).total;
  }

  get certificatesCost(): number {
    if (!this.certificatesSelected || !this.certificatesQuantity) return 0;
    return Number(this.certificatesExtra?.item_price ?? 0) * this.certificatesQuantity;
  }

  get drinksCost(): number {
    if (!this.firstToastSelected) return 0;
    let total = 0;
    for (const d of this.drinkExtras) {
      const qty = this.drinkSelections[d.item_id] ?? 0;
      if (qty > 0) total += d.item_price * qty;
    }
    return total;
  }

  /** First-toast flat fee — added once when at least one paid drink is selected. */
  get firstToastFee(): number {
    if (!this.firstToastSelected) return 0;
    const anyDrinkSelected = this.drinkExtras.some((d) => (this.drinkSelections[d.item_id] ?? 0) > 0);
    if (!anyDrinkSelected) return 0;
    return Number(this.firstToastExtra?.item_price ?? 0);
  }

  get addOnsCost(): number {
    return this.confettiCost + this.drinksCost + this.firstToastFee + this.certificatesCost;
  }

  get totalAdditionalCost(): number {
    return this.extensionPrice + this.addOnsCost;
  }

  // ---- Human-readable summary text (shared by summary + confirmation pages) ----

  get extensionSummaryText(): string {
    const opt = this.selectedExtension;
    return opt ? `Add ${opt.shortLabel} - £${this.extensionPrice.toFixed(2)}` : '—';
  }

  get confettiSummaryText(): string {
    if (!this.confettiSelected || !this.confettiQuantity) return 'No';
    return `${this.confettiQuantity} sachets - £${this.confettiCost.toFixed(2)}`;
  }

  get firstToastSummaryText(): string {
    if (!this.firstToastSelected) return 'No';
    const rows: string[] = [];
    for (const d of this.drinkExtras) {
      const qty = this.drinkSelections[d.item_id] ?? 0;
      if (qty > 0) rows.push(`${qty} × ${d.item_name}`);
    }
    if (rows.length === 0) return 'No';
    const total = (this.drinksCost + this.firstToastFee).toFixed(2);
    return `${rows.join(', ')} - £${total}`;
  }

  get waterCordialSummaryText(): string {
    return this.firstToastSelected && this.waterOrCordialSelected === 'yes'
      ? 'Yes (free)'
      : 'None';
  }

  get certificatesSummaryText(): string {
    if (!this.certificatesSelected || !this.certificatesQuantity) return 'No';
    return `${this.certificatesQuantity} certificates - £${this.certificatesCost.toFixed(2)}`;
  }

  // ---- Snapshot persistence (survives external payment redirect) ----

  static readonly SNAPSHOT_KEY = 'lastExtendRequest';

  writeSnapshot(path: 'confirm' | 'enquire', bookingReference?: string): void {
    const snapshot: ExtendSummarySnapshot = {
      path,
      bookingReference,
      extensionSummary: this.extensionSummaryText,
      confettiSummary: this.confettiSummaryText,
      firstToastSummary: this.firstToastSummaryText,
      waterCordialSummary: this.waterCordialSummaryText,
      certificatesSummary: this.certificatesSummaryText,
      totalAdditionalCost: this.totalAdditionalCost,
    };
    localStorage.setItem(ExtendStateService.SNAPSHOT_KEY, JSON.stringify(snapshot));
  }

  readSnapshot(): ExtendSummarySnapshot | null {
    const raw = localStorage.getItem(ExtendStateService.SNAPSHOT_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as ExtendSummarySnapshot;
    } catch {
      return null;
    }
  }

  clearSnapshot(): void {
    localStorage.removeItem(ExtendStateService.SNAPSHOT_KEY);
  }

  reset(): void {
    this.extensionOption = null;
    this.summaryConfirmation = null;
    this.paymentChoice = null;
    this.confettiSelected = false;
    this.confettiQuantity = null;
    this.firstToastSelected = false;
    this.drinkSelections = {};
    this.waterOrCordialSelected = null;
    this.certificatesSelected = false;
    this.certificatesQuantity = null;
    this.availabilityConfirmed = null;
    this.checkingAvailability = false;
    this.latestCheckId++;
    // Keep extrasLoaded + pricingLoaded caches and the resolved per-option costs
    // — same journey, same booking. extensionCost / roomHireNewCost are getters
    // off the (now-cleared) selectedExtension, so they read 0 again until reselect.
  }
}
